// Pure piñata game rules. No DOM, no Pixi — the multiplayer server will run
// this exact module as the authority, so keep it side-effect free.

export type PinataStatus = 'intact' | 'exploded'

/** Per-piñata tuning, set by the admin. */
export type PinataRules = {
	maxHealth: number
	damageMin: number
	damageMax: number
	comboEnabled: boolean
	/** Timing meter: wound-up swings in the green crit, anything else is a weak hit */
	powerSwing: boolean
}

export type PinataState = {
	rules: PinataRules
	health: number
	maxHealth: number
	hits: number
	crits: number
	weakHits: number
	combo: number
	status: PinataStatus
	lastHitAt: number | null
}

/** How a power swing landed. Outside power swing mode every hit is 'normal'. */
export type HitStrength = 'normal' | 'crit' | 'weak'

/** A whack. x/y are in piñata-local 0..1 space (0,0 = top-left of the image). */
export type Hit = {
	x: number
	y: number
	at: number
	/** 0..1 roll used for damage spread; injected so the logic stays deterministic */
	roll: number
	/** What the swing is asking for; applyHit may downgrade a crit to weak */
	strength?: HitStrength
}

export type HitEvent =
	| { type: 'ignored'; reason: 'cooldown' | 'exploded' }
	| {
			type: 'hit'
			damage: number
			combo: number
			strength: HitStrength
			stage: number
			stageChanged: boolean
	  }
	| { type: 'explode'; damage: number; combo: number; strength: HitStrength }

export const HIT_COOLDOWN_MS = 120
/**
 * A crit needs a wind-up: this long since the last swing. Mashing still lands
 * hits, but only weak ones, so spamming can't farm crits.
 */
export const POWER_WINDUP_MS = 450
export const COMBO_WINDOW_MS = 600
export const DEFAULT_RULES: PinataRules = {
	maxHealth: 100,
	damageMin: 6,
	damageMax: 10,
	comboEnabled: true,
	powerSwing: false,
}
export const MAX_COMBO = 5
/** Each combo step adds this fraction of damage (combo 5 = 2x). */
export const COMBO_BONUS = 0.25
export const CRIT_MULTIPLIER = 3
/** Power swings outside the green still land, for a third of the damage (min 1) */
export const WEAK_MULTIPLIER = 1 / 3
export const STAGE_THRESHOLDS = [0.8, 0.6, 0.4, 0.2] as const

/** The power meter: a marker sweeping 0→1→0; the green zone sits in the middle. */
export const POWER_METER = {
	/** Time for one sweep from one end to the other */
	sweepMs: 900,
	zoneStart: 0.4,
	zoneEnd: 0.6,
} as const

export function createPinataState(rules: Partial<PinataRules> = {}): PinataState {
	const full = { ...DEFAULT_RULES, ...rules }
	return {
		rules: full,
		health: full.maxHealth,
		maxHealth: full.maxHealth,
		hits: 0,
		crits: 0,
		weakHits: 0,
		combo: 0,
		status: 'intact',
		lastHitAt: null,
	}
}

/** 0 = pristine … 4 = barely hanging on. */
export function damageStage(state: Pick<PinataState, 'health' | 'maxHealth'>) {
	const ratio = state.health / state.maxHealth
	return STAGE_THRESHOLDS.filter((t) => ratio <= t).length
}

/** Marker position (0..1) a given time after the meter started. */
export function meterPosition(elapsedMs: number, sweepMs: number = POWER_METER.sweepMs) {
	const phase = (Math.max(0, elapsedMs) / sweepMs) % 2
	return phase < 1 ? phase : 2 - phase
}

export function isSweetSpot(position: number) {
	return position >= POWER_METER.zoneStart && position <= POWER_METER.zoneEnd
}

/** Settles how a swing lands: only power swing mode has crits and weak hits. */
function resolveStrength(rules: PinataRules, requested: HitStrength | undefined, sinceLast: number) {
	if (!rules.powerSwing) return 'normal'
	if (requested === 'crit' && sinceLast >= POWER_WINDUP_MS) return 'crit'
	return 'weak'
}

const STRENGTH_MULTIPLIER: Record<HitStrength, number> = {
	normal: 1,
	crit: CRIT_MULTIPLIER,
	weak: WEAK_MULTIPLIER,
}

export function applyHit(
	state: PinataState,
	hit: Hit,
): { state: PinataState; event: HitEvent } {
	if (state.status === 'exploded') {
		return { state, event: { type: 'ignored', reason: 'exploded' } }
	}
	const sinceLast = state.lastHitAt === null ? Infinity : hit.at - state.lastHitAt
	if (sinceLast < HIT_COOLDOWN_MS) {
		return { state, event: { type: 'ignored', reason: 'cooldown' } }
	}

	const { damageMin, damageMax, comboEnabled, powerSwing } = state.rules
	const strength = resolveStrength(state.rules, hit.strength, sinceLast)
	// Power swing is about timing, not speed, so combos don't apply there.
	const combo =
		comboEnabled && !powerSwing && sinceLast <= COMBO_WINDOW_MS
			? Math.min(state.combo + 1, MAX_COMBO)
			: 0
	const roll = Math.min(Math.max(hit.roll, 0), 1)
	// Rounded before the bonuses so min === max gives exactly that per hit.
	const base = Math.round(damageMin + roll * (damageMax - damageMin))
	const damage = Math.max(
		1,
		Math.round(base * (1 + combo * COMBO_BONUS) * STRENGTH_MULTIPLIER[strength]),
	)
	const health = Math.max(0, state.health - damage)
	const next: PinataState = {
		...state,
		health,
		hits: state.hits + 1,
		crits: state.crits + (strength === 'crit' ? 1 : 0),
		weakHits: state.weakHits + (strength === 'weak' ? 1 : 0),
		combo,
		lastHitAt: hit.at,
		status: health === 0 ? 'exploded' : 'intact',
	}

	if (next.status === 'exploded') {
		return { state: next, event: { type: 'explode', damage, combo, strength } }
	}
	const stage = damageStage(next)
	return {
		state: next,
		event: {
			type: 'hit',
			damage,
			combo,
			strength,
			stage,
			stageChanged: stage !== damageStage(state),
		},
	}
}

/**
 * Rough hits-to-break range for the admin form. Normal mode: best case with a
 * full combo, worst without. Power swing: all crits vs. all weak hits.
 */
export function estimateHits(rules: PinataRules) {
	const perHit = (damage: number, multiplier: number) =>
		Math.max(1, Math.round(damage * multiplier))
	const best = rules.powerSwing
		? CRIT_MULTIPLIER
		: rules.comboEnabled
			? 1 + MAX_COMBO * COMBO_BONUS
			: 1
	const worst = rules.powerSwing ? WEAK_MULTIPLIER : 1
	return {
		fastest: Math.ceil(rules.maxHealth / perHit(rules.damageMax, best)),
		slowest: Math.ceil(rules.maxHealth / perHit(rules.damageMin, worst)),
	}
}
